// Prompt Mestre V3.0 — Front-End | Gestão e Impressão de Cartas de Circularização.
// Contrato de dados mockado (seção 13 do documento — "Modelo de Dados Front-End para
// Integração Futura"). Nenhuma tabela, endpoint ou contrato definitivo de API é criado aqui:
// esta interface só organiza o estado/componentes do Front-End enquanto o Back-End (regras
// automáticas de seleção, persistência, trilha de auditoria em banco) não existe.

export type CategoriaCircularizacao =
  | 'FORNECEDORES'
  | 'BANCOS'
  | 'JURIDICO'
  | 'CLIENTES'
  | 'PARTES_RELACIONADAS';

// Ordem de exibição pedida pelo usuário: Bancos, Clientes, Fornecedores, Partes Relacionadas,
// Jurídico (os cards da Central e as abas de Modelos de Carta seguem essa mesma ordem, já que
// ambos mapeiam direto sobre este array).
export const CATEGORIAS_CIRCULARIZACAO: {
  value: CategoriaCircularizacao;
  label: string;
  descricaoTemplate: string;
}[] = [
  {
    value: 'BANCOS',
    label: 'Bancos e Instituições Financeiras',
    descricaoTemplate:
      'Confirmação de contas, aplicações, empréstimos, financiamentos, garantias e demais operações financeiras.',
  },
  {
    value: 'CLIENTES',
    label: 'Clientes',
    descricaoTemplate: 'Confirmação de saldos a receber, títulos e operações comerciais.',
  },
  {
    value: 'FORNECEDORES',
    label: 'Fornecedores',
    descricaoTemplate: 'Confirmação de saldos e obrigações mantidos na data-base da auditoria.',
  },
  {
    value: 'PARTES_RELACIONADAS',
    label: 'Partes Relacionadas',
    descricaoTemplate:
      'Confirmação de saldos, empréstimos, mútuos, garantias, compromissos e operações com partes relacionadas.',
  },
  {
    value: 'JURIDICO',
    label: 'Assessores Jurídicos',
    descricaoTemplate:
      'Solicitação de informações sobre processos, contingências, avaliação de riscos e assuntos jurídicos relevantes.',
  },
];

export type OrigemSelecao = 'SUGESTAO_SISTEMA' | 'INCLUSAO_MANUAL';

// Seção 3.2 — "Situação": Pendente, Revisada, Incluída ou Excluída.
export type SituacaoRevisao = 'PENDENTE' | 'REVISADA' | 'INCLUIDA' | 'EXCLUIDA';

// Seção 10/11 — estado de preparação da carta (nunca confundir com confirmação externa recebida).
export type SituacaoPreparacaoCarta = 'NAO_INICIADA' | 'EM_ANDAMENTO' | 'PRONTA' | 'NAO_SE_APLICA';

export type OperacaoHistorico = 'INCLUSAO' | 'EXCLUSAO' | 'ALTERACAO' | 'RESTAURACAO';

export interface IContatoCircularizacao {
  id: string;
  nomeCompleto: string;
  emailPrincipal: string;
  telefone: string;
  cargo?: string;
  departamento?: string;
  telefoneAlternativo?: string;
  emailAlternativo?: string;
  observacoes?: string;
  contatoPrincipal: boolean;
}

export interface IRegistroCircularizacao {
  id: string;
  empresaAuditadaId: number;
  categoria: CategoriaCircularizacao;

  razaoSocialOriginal: string;
  razaoSocialExibicao: string;
  nomeFantasia?: string;
  cnpj: string;

  origemSelecao: OrigemSelecao;
  justificativaOriginal: string;
  referenciaRegraSelecao?: string;

  situacaoRevisao: SituacaoRevisao;
  justificativaInclusaoManual?: string;
  justificativaExclusao?: string;
  justificativaAlteracao?: string;

  contatos: IContatoCircularizacao[];
  // Seção 6/7 — planejamento é por destinatário (empresa), referenciando o contato responsável.
  contatoResponsavelId?: string;
  dataPrevistaEnvio?: string;
  prazoResposta?: string;
  situacaoPreparacaoCarta: SituacaoPreparacaoCarta;

  excluida: boolean;
  alteradaPeloAuditor: boolean;

  criadoEm: string;
  atualizadoEm: string;
}

export interface IHistoricoAjusteCircularizacao {
  id: string;
  registroId: string;
  empresaNome: string;
  categoria: CategoriaCircularizacao;
  operacao: OperacaoHistorico;
  informacaoOriginal?: string;
  informacaoAtualizada?: string;
  justificativa: string;
  responsavel: string;
  dataHora: string;
}

export interface IIndicadoresCircularizacao {
  empresasSugeridas: number;
  empresasIncluidasManualmente: number;
  empresasExcluidas: number;
  empresasAtivas: number;
  cadastrosCompletos: number;
  cartasPreparadas: number;
  cartasPendentes: number;
  categoriasNaoAplicaveis: number;
  // Destinatários ativos cujo prazo de resposta já passou (seção 7.1 — "identificar datas
  // vencidas"). Usado também para o estado "PENDENTE" da barra de progressão (seção 2).
  prazosVencidos: number;
  // Seção 11.1 — "Percentual de Preparação = Cartas Prontas ÷ Cartas Previstas para os
  // Destinatários Ativos × 100". null quando não há destinatários ativos (não indicar 100%
  // automaticamente nesse caso — exigir definição explícita de aplicabilidade).
  percentualPreparacao: number | null;
}

export interface IAlertaInconsistencia {
  registroId: string;
  // Antes era um union literal fechado (apenas os tipos que o mock gerava); o Back-End real
  // (CircularizacaoRegistroService.ListarAlertasInconsistenciaAsync) usa seu próprio conjunto de
  // códigos (CNPJ_AUSENTE/SEM_CONTATO/SEM_PLANEJAMENTO). Nada no Front-End decide com base neste
  // valor (AlertasInconsistenciaPanel só exibe `mensagem`), então ficou como string livre.
  tipo: string;
  mensagem: string;
}

export interface INovoRegistroManual {
  categoria: CategoriaCircularizacao;
  razaoSocial: string;
  nomeFantasia?: string;
  cnpj: string;
  justificativaInclusao: string;
  observacoesComplementares?: string;
}

// Épico Backend (Cards 08-12) — ciclo de vida completo da carta após a preparação/impressão local
// (seção 8 do épico: 19 estados, aqui agrupados pelos 3 blocos que o Front-End realmente precisa
// distinguir para decidir que ação mostrar).
export type CartaStatus =
  | 'AGUARDANDO_PREPARACAO'
  | 'PRONTA'
  | 'APROVADA'
  | 'ENVIO_AGENDADO'
  | 'ENVIADA'
  | 'FALHA_ENVIO'
  | 'AGUARDANDO_RESPOSTA'
  | 'A_VENCER'
  | 'VENCIDA'
  | 'RESPOSTA_RECEBIDA'
  | 'EM_ANALISE'
  | 'CONCORDANTE'
  | 'DIVERGENTE'
  | 'PARCIAL'
  | 'INCONCLUSIVA'
  | 'RECUSA'
  | 'PROCEDIMENTO_ALTERNATIVO'
  | 'CONCLUIDA'
  | 'CANCELADA';

export interface ICartaCircularizacao {
  id: string;
  registroId: string;
  numeroCarta: string;
  status: CartaStatus;
  storageKey?: string;
  aprovadaEm?: string;
  criadoEm: string;
}

export type TipoTemplateCircularizacao = 'INICIAL' | 'LEMBRETE_D2' | 'COBRANCA_D1';
export type PerfilTemplateCircularizacao = 'FORMAL' | 'OBJETIVO' | 'DETALHADO' | 'CORDIAL';
export type EscopoTemplateCircularizacao = 'INSTITUCIONAL' | 'PESSOAL' | 'COMPARTILHADO';

export interface ITemplateVersaoCircularizacao {
  id: string;
  versao: number;
  assunto: string;
  corpoHtml: string;
  vigente: boolean;
}

export interface ITemplateCircularizacao {
  id: string;
  categoria: CategoriaCircularizacao;
  tipo: TipoTemplateCircularizacao;
  perfil: PerfilTemplateCircularizacao;
  nome: string;
  escopo: EscopoTemplateCircularizacao;
  institucional: boolean;
  versaoVigente?: ITemplateVersaoCircularizacao;
}

export interface IPoliticaCobranca {
  id: string;
  categoria?: CategoriaCircularizacao;
  ativa: boolean;
  diasLembretePreventivo: number;
  diasCobrancaAtraso: number;
  considerarDiasUteis: boolean;
  autorizadaEm?: string;
}

// Classificações possíveis de uma resposta recebida (Card 12 — RN-010).
export type StatusValidacaoResposta = 'CANDIDATA' | 'EM_ANALISE' | 'CONCORDANTE' | 'DIVERGENTE' | 'PARCIAL' | 'INCONCLUSIVA' | 'RECUSA';

export interface IRespostaCircularizacao {
  id: string;
  cartaId: string;
  remetenteEmail: string;
  assunto?: string;
  tipo: string;
  statusValidacao: StatusValidacaoResposta;
  recebidoEm: string;
}
